import localforage from "localforage";
import Vue from "vue";
import { Module } from "vuex";
import { FeedEntry } from "./feed";

interface QueueEntry extends FeedEntry {
  enqueued: Date;
}

// #region Operation Status

const NEVER_QUEUED = "never-queued";

interface NeverQueued {
  status: typeof NEVER_QUEUED;
}

const ENQUEUE_PENDING = "enqueue-pending";

interface EnqueuePending {
  status: typeof ENQUEUE_PENDING;
}

export const ENQUEUE_FAILED = "enqueue-failed";

interface EnqueueFailed {
  status: typeof ENQUEUE_FAILED;
  error: Error;
}

export const ENQUEUED = "enqueued";

interface Enqueued {
  status: typeof ENQUEUED;
}

const DEQUEUE_PENDING = "dequeue-pending";

interface DequeuePending {
  status: typeof DEQUEUE_PENDING;
}

export const DEQUEUE_FAILED = "dequeue-failed";

interface DequeueFailed {
  status: typeof DEQUEUE_FAILED;
  error: Error;
}

export const DEQUEUED = "dequeued";

interface Dequeued {
  status: typeof DEQUEUED;
}

export type QueueEntryStatus =
  | NeverQueued
  | EnqueuePending
  | EnqueueFailed
  | Enqueued
  | DequeuePending
  | DequeueFailed
  | Dequeued;

// #endregion

// #region Mutations

interface EnqueuePendingMutation {
  type: typeof ENQUEUE_PENDING;
  id: string;
}

export interface EnqueueFailedMutation {
  type: typeof ENQUEUE_FAILED;
  id: string;
  error: Error;
}

export interface EnqueuedMutation {
  type: typeof ENQUEUED;
  entry: QueueEntry;
  loaded: boolean;
}

interface DequeuePendingMutation {
  type: typeof DEQUEUE_PENDING;
  id: string;
}

export interface DequeueFailedMutation {
  type: typeof DEQUEUE_FAILED;
  id: string;
  error: Error;
}

export interface DequeuedMutation {
  type: typeof DEQUEUED;
  id: string;
}

// #endregion

// #region Actions

export interface EnqueueAction {
  entry: FeedEntry;
}

export interface DequeueAction {
  id: string;
}

// #endregion

export interface ModuleState {
  list: QueueEntry[];
  status: {
    [id: string]: QueueEntryStatus;
  };
}

function delay() {
  return new Promise(resolve => setTimeout(resolve, 500));
}

const module: Module<ModuleState, {}> = {
  namespaced: true,
  state: {
    list: [],
    status: {}
  },
  getters: {
    getStatusById: state => (id: string) =>
      state.status[id] || { status: NEVER_QUEUED }
  },
  mutations: {
    [ENQUEUE_PENDING](state, { id }: EnqueuePendingMutation) {
      const status: EnqueuePending = { status: ENQUEUE_PENDING };
      Vue.set(state.status, id, status);
    },
    [ENQUEUE_FAILED](state, { id, error }: EnqueueFailedMutation) {
      const status: EnqueueFailed = { status: ENQUEUE_FAILED, error };
      Vue.set(state.status, id, status);
    },
    [ENQUEUED](state, { entry }: EnqueuedMutation) {
      const status: Enqueued = { status: ENQUEUED };
      Vue.set(state.status, entry.id, status);
      state.list.unshift(entry);
    },
    [DEQUEUE_PENDING](state, { id }: DequeuePendingMutation) {
      const status: DequeuePending = { status: DEQUEUE_PENDING };
      Vue.set(state.status, id, status);
    },
    [DEQUEUE_FAILED](state, { id, error }: DequeueFailedMutation) {
      const status: DequeueFailed = { status: DEQUEUE_FAILED, error };
      Vue.set(state.status, id, status);
    },
    [DEQUEUED](state, { id }: DequeuedMutation) {
      const status: Dequeued = { status: DEQUEUED };
      Vue.set(state.status, id, status);
      state.list = state.list.filter(entry => entry.id === id);
    }
  },
  actions: {
    async enqueue({ commit }, { entry }: EnqueueAction) {
      const id = entry.id;
      commit<EnqueuePendingMutation>({ type: ENQUEUE_PENDING, id });
      const queueEntry: QueueEntry = { ...entry, enqueued: new Date() };

      try {
        await Promise.all([delay(), localforage.setItem(id, queueEntry)]);
        commit<EnqueuedMutation>({
          type: ENQUEUED,
          entry: queueEntry,
          loaded: false
        });
      } catch (error) {
        commit<EnqueueFailedMutation>({ type: ENQUEUE_FAILED, id, error });
      }
    },
    async dequeue({ commit }, { id }: DequeueAction) {
      commit<DequeuePendingMutation>({ type: DEQUEUE_PENDING, id });

      try {
        await Promise.all([delay(), localforage.removeItem(id)]);
        commit<DequeuedMutation>({ type: DEQUEUED, id });
      } catch (error) {
        commit<DequeueFailedMutation>({ type: DEQUEUE_FAILED, id, error });
      }
    },
    async load({ commit }) {
      const entries: QueueEntry[] = [];
      await localforage.iterate<QueueEntry, void>(value => {
        entries.push(value);
      });

      // sorts in reverse chronological order (oldest first)
      entries.sort(
        (first, second) => second.enqueued.valueOf() - first.enqueued.valueOf()
      );

      for (const entry of entries) {
        commit<EnqueuedMutation>({ type: ENQUEUED, entry, loaded: true });
      }
    }
  }
};

export default module;
